import React from "react";
import { connect, type ConnectedProps } from "react-redux";
import { get, isEmpty, some } from "lodash";
import { Card, Col, Form, Row } from "react-bootstrap";
import { Formik } from "formik";
import { ScrollToFieldError } from "utils/form";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { createLoadingSelector } from "store/selectors";
import { activeBankSelector } from "store/selectors/bank";
import { accountConstants } from "actions/types";
import { formatAmount, formatCurrency } from "utils/number";
import { getIraAccountIdSelector } from "store/selectors/user";
import { contributionAbilitySelector } from "store/selectors/contribution";
import { accountTypesToEnglishMapping } from "statics/accountTypes";
import type { RootState } from "types/state";

import IconSpinner from "components/IconSpinner";
import Button from "components/Button";
import AccountBox from "./AccountBox";
import ConfirmTransfer from "./ConfirmTransfer";
import WithdrawalReasonSelect from "./WithdrawalReasonSelect";
import Alert from "components/Alert";

import "./TransferWidget.scss";

import * as yup from "yup";

/**
 * The from/to account shapes stored in state. Populated from the
 * `bankAccount` (UserBank) and `iraAccount` (IraAccount) props, both declared
 * as `object` in the original propTypes — only the fields read here are typed.
 */
interface TransferAccount {
  accountType?: string | null;
  bankName?: string | null;
  bankAlias?: string | null;
  balance?: number | null;
  accountId?: string | null;
  id?: string | null;
  [key: string]: unknown;
}

/** Formik values: the state slice fed into initialValues plus taxYear. */
interface TransferWidgetFormValues {
  fromAccount: TransferAccount;
  toAccount: TransferAccount;
  amount?: number;
  frequency: string;
  transferDate: string;
  showConfirmation: boolean;
  reason: string;
  isFullDisbursement: boolean;
  taxYear?: string;
}

interface TransferWidgetOwnProps {
  error?: string;
  onSuccess?: () => void;
  onClose?: React.MouseEventHandler<HTMLButtonElement>;
  isApplyToPriorYear?: boolean;
  depositOnly?: boolean;
  // declared in propTypes; supplied by the parent via spread (...options)
  values?: object;
  bankId?: string;
}

interface TransferWidgetState {
  fromAccount: TransferAccount;
  toAccount: TransferAccount;
  amount?: number;
  frequency: string;
  transferDate: string;
  showConfirmation: boolean;
  reason: string;
  isFullDisbursement: boolean;
  // read in JSX (this.state.accountAgreement) but never initialized — a
  // pre-existing quirk, so it is optional/unknown here, not added to init
  accountAgreement?: unknown;
}

function getLastFourOfAccount(accountId?: string | null) {
  if (!accountId) {
    return "";
  }
  return accountId.substring(accountId.length - 4, accountId.length);
}

const loadingSelector = createLoadingSelector(accountConstants.GET_ACCOUNTS);

const mapStateToProps = (state: RootState) => {
  const userId = state.user.id;
  const bankAccount = activeBankSelector(state) || {};

  const accounts = state.accounts.iraAccounts;
  const iraAccountId = getIraAccountIdSelector(state);
  const iraAccount = accounts.find((account) => account.id === iraAccountId);
  return {
    iraAccountId,
    bankAccount,
    iraAccount,
    userId,
    isFetching: loadingSelector(state),
    contributionAbility: contributionAbilitySelector(state),
  };
};

const connector = connect(mapStateToProps, null);

type PropsFromRedux = ConnectedProps<typeof connector>;

type TransferWidgetProps = WithApolloClient<
  PropsFromRedux & TransferWidgetOwnProps
>;

class TransferWidget extends React.PureComponent<
  TransferWidgetProps,
  TransferWidgetState
> {
  static defaultProps = {
    isApplyToPriorYear: false,
  };

  static getDerivedStateFromProps(
    nextProps: TransferWidgetProps,
    prevState: TransferWidgetState
  ): Partial<TransferWidgetState> {
    const newState: Partial<TransferWidgetState> = {};
    if (isEmpty(prevState.fromAccount) && !isEmpty(nextProps.bankAccount)) {
      newState.fromAccount = nextProps.bankAccount;
    }

    if (isEmpty(prevState.toAccount) && !isEmpty(nextProps.iraAccount)) {
      newState.toAccount = nextProps.iraAccount;
    }

    return newState;
  }

  constructor(props: TransferWidgetProps) {
    super(props);

    this.state = {
      fromAccount: {},
      toAccount: {},
      amount: undefined,
      frequency: "Just this once",
      transferDate: "Today",
      showConfirmation: false,
      reason: "",
      isFullDisbursement: false,
    };
  }

  _getTaxYear = (): unknown => {
    if (this.props.isApplyToPriorYear) {
      return get(this.props, "contributionAbility.priorYear.taxYear");
    }
    return get(this.props, "contributionAbility.currentYear.taxYear");
  };

  _getAmountRemaining = (): number => {
    if (this.props.isApplyToPriorYear) {
      return get(
        this.props,
        "contributionAbility.priorYear.amountRemaining",
        0
      );
    }
    // contributionAbility is typed `object`, so lodash get resolves the dotted
    // path to undefined statically; the original (untyped) returned it as-is
    return get(
      this.props,
      "contributionAbility.currentYear.amountRemaining"
    ) as unknown as number;
  };

  _getContributionLimits = (): number => {
    if (this.props.isApplyToPriorYear) {
      return get(this.props, "contributionAbility.priorYear.limit", 0);
    }
    return get(
      this.props,
      "contributionAbility.currentYear.limit"
    ) as unknown as number;
  };

  _reverseAccounts = () => {
    const toAccount = { ...this.state.fromAccount };
    const fromAccount = { ...this.state.toAccount };

    this.setState({
      fromAccount,
      toAccount,
    });
  };

  _setModalState = (showModal: boolean) => {
    this.setState({
      showConfirmation: showModal,
    });
  };

  onSubmit = (values: TransferWidgetFormValues) => {
    this.setState({ ...values });
    this._setModalState(true);
  };

  _onAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const amount = +event.target.value;
    this.setState({
      amount: formatAmount(amount),
    });
  };

  renderContent() {
    const isIraWithdrawal = this.state.fromAccount.accountType === "IRA";

    const schema = yup.object({
      amount: yup
        .number()
        .when("isFullDisbursement", {
          is: true,
          then: (s) => s,
          otherwise: (s) =>
            s
              .required()
              .min(1, "Amount must be greater than 0.")
              .max(
                this._getAmountRemaining(),
                `Amount must be less than or equal to ${formatCurrency(
                  this._getAmountRemaining()
                )}.`
              ),
        })
        .label("Amount"),
      transferDate: yup.string().label("Transfer Date").required(),
      frequency: yup.string().label("Frequency").required(),
      isFullDisbursement: yup.boolean().label("Withdrawal all funds"),
      fromAccount: yup.object().shape({
        accountType: yup.string(),
      }),
      reason: yup.string().when("fromAccount", {
        is: (acct: TransferAccount) => acct.accountType === "IRA",
        then: (s) => s.required(),
        otherwise: (s) => s,
      }),
    });
    return (
      <Formik<TransferWidgetFormValues>
        validateOnChange={false}
        validationSchema={schema}
        onSubmit={(values) => {
          this.onSubmit(values);
        }}
        enableReinitialize={true}
        initialValues={{
          ...this.state,
          // _getTaxYear() resolves via lodash get against the `object`-typed
          // contributionAbility prop, so it is unknown; rendered as a string
          taxYear: this._getTaxYear() as string,
        }}
      >
        {({
          handleSubmit,
          handleChange,
          values,
          touched,
          errors,
          handleBlur,
        }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <ScrollToFieldError />
            <div className="accounts-and-flip-icon">
              <AccountBox
                isFrom
                bankName={this.state.fromAccount.bankName ?? undefined}
                isBank={this.state.fromAccount.accountType !== "IRA"}
                accountType={this.state.fromAccount.accountType ?? undefined}
                accountName={this.state.fromAccount.bankAlias ?? undefined}
                balance={this.state.fromAccount.balance ?? undefined}
                lastFourOfAccount={getLastFourOfAccount(
                  this.state.fromAccount.accountId
                )}
              />
              <AccountBox
                bankName={this.state.toAccount.bankName ?? undefined}
                isBank={this.state.toAccount.accountType !== "IRA"}
                accountType={this.state.toAccount.accountType ?? undefined}
                accountName={this.state.toAccount.bankAlias ?? undefined}
                balance={this.state.toAccount.balance ?? undefined}
                lastFourOfAccount={getLastFourOfAccount(
                  this.state.toAccount.accountId
                )}
              />
            </div>
            {isIraWithdrawal && (
              <Row className="mb-3">
                <Form.Group as={Col} sm={12} controlId="isFullDisbursement">
                  <Form.Check
                    type="checkbox"
                    id="isFullDisbursement"
                    name="isFullDisbursement"
                    label="Withdraw all available funds"
                    value={this.state.accountAgreement as string}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Form.Group>
              </Row>
            )}
            <Form.Group as={Col} controlId="amount" style={{ paddingTop: 15 }}>
              <Form.Label>Amount</Form.Label>

              <div className="mb-3 position-relative">
                <span className="currency-sign">$</span>
                <Form.Control
                  name="amount"
                  disabled={isIraWithdrawal && values.isFullDisbursement}
                  placeholder="0.00"
                  value={values.amount}
                  onChange={this._onAmountChange}
                  onBlur={handleBlur}
                  type="number"
                  isInvalid={touched.amount && !!errors.amount}
                  isValid={touched.amount && !errors.amount}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.amount}
                </Form.Control.Feedback>
                {!isIraWithdrawal && (
                  <Form.Text muted>
                    You can contribute{" "}
                    <span className="amount-remaining">
                      {formatCurrency(this._getAmountRemaining())}{" "}
                    </span>
                    before reaching your maximum.
                  </Form.Text>
                )}
              </div>
            </Form.Group>

            {!this.props.depositOnly && (
              <Form.Group as={Col} controlId="tax-year">
                <Row className="mb-3">
                  <Form.Label>Tax Year</Form.Label>
                </Row>

                <Row className="mb-3">
                  <Form.Control
                    name="taxYear"
                    disabled
                    value={values.taxYear}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.taxYear && !!errors.taxYear}
                    isValid={touched.taxYear && !errors.taxYear}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.taxYear}
                  </Form.Control.Feedback>
                  <Form.Text style={{ color: "#1D99A9", fontStyle: "italic" }}>
                    You are initiating a transfer to your {values.taxYear}{" "}
                    {
                      (accountTypesToEnglishMapping as Record<string, string>)[
                        this.state.toAccount.accountType ?? ""
                      ]
                    }
                    .
                  </Form.Text>
                </Row>
              </Form.Group>
            )}
            {isIraWithdrawal && (
              <Form.Group as={Col} sm={4} controlId="formBasicReason">
                <Form.Label>Withdrawal Reason</Form.Label>
                <WithdrawalReasonSelect
                  value={values.reason}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.reason}
                </Form.Control.Feedback>
              </Form.Group>
            )}
            {/* {!isIraWithdrawal && (
              <>
                <Form.Group as={Col} controlId="transferDate">
                  <Row className="mb-3">
                    <Form.Label>Transfer Date</Form.Label>
                  </Row>

                  <Row className="mb-3">
                    <Form.Control
                      name="transferDate"
                      disabled
                      value={values.transferDate}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.transferDate && !!errors.transferDate}
                      isValid={touched.transferDate && !errors.transferDate}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.transferDate}
                    </Form.Control.Feedback>
                  </Row>
                </Form.Group>
                <Form.Group as={Col} controlId="frequency">
                  <Row className="mb-3">
                    <Form.Label>Frequency</Form.Label>
                  </Row>

                  <Row className="mb-3">
                    <Form.Control
                      name="frequency"
                      disabled
                      value={values.frequency}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.frequency && !!errors.frequency}
                      isValid={touched.frequency && !errors.frequency}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.frequency}
                    </Form.Control.Feedback>
                  </Row>
                </Form.Group>
              </>
            )} */}

            <section className="form-sec-2col" style={{ paddingLeft: 10 }}>
              {this.props.error && (
                <Alert type="error" msg={this.props.error} />
              )}
              <div className="submit-row btn-row">
                <Button
                  name="cancel"
                  color="cancel"
                  btnLabel="Close"
                  type="button"
                  onClick={this.props.onClose}
                />
                <Button name="submit" withArrow={true} btnLabel="Continue" />
              </div>
            </section>
          </Form>
        )}
      </Formik>
    );
  }

  render() {
    let content;
    const isIraWithdrawal = this.state.fromAccount.accountType === "IRA";

    if (this.props.isFetching) {
      content = <IconSpinner centered />;
    } else if (some([this.props.iraAccount, this.props.bankAccount], isEmpty)) {
      return null;
    } else if (this.state.showConfirmation) {
      return (
        <ConfirmTransfer
          isWithdrawal={isIraWithdrawal}
          // state.amount is optional (initialized undefined) but ConfirmTransfer
          // requires a number; pass the state through as the original did
          values={
            this.state as React.ComponentProps<typeof ConfirmTransfer>["values"]
          }
          show={this.state.showConfirmation}
          // @ts-expect-error pre-existing: ConfirmTransfer declares neither
          // iraAccountId (it gets that from its own redux connect) nor userId
          // as own-props, so both are excess — JSX flags the first attribute
          // (iraAccountId). Preserved to keep runtime behavior.
          iraAccountId={this.props.iraAccount?.id}
          bankId={this.props.bankAccount.id}
          userId={this.props.userId}
          onClose={() => {
            this._setModalState(false);
          }}
          onSuccess={this.props.onSuccess}
          isApplyToPriorYear={this.props.isApplyToPriorYear}
        />
      );
    } else {
      content = this.renderContent();
    }

    return (
      <Card className="transfer-card">
        <Card.Body>
          <Card.Title className="transfer-card-title">
            One-time transfer
          </Card.Title>
          {content}
        </Card.Body>
      </Card>
    );
  }
}

export default connector(withApollo<TransferWidgetProps>(TransferWidget));
